import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import RichText from "@components/RichText";

import {
  TeamInfoContent,
  TeamInfoImage,
  TeamInfoMain,
  TeamInfoText,
  TeamInfoTitle,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const getAssetUrl = (asset) => {
  const url = asset?.file?.url;

  if (!url) return null;

  return url.startsWith("//") ? `https:${url}` : url;
};

const OurTeamInfo = ({ image, title, id }) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const textRef = useRef(null);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-team";
  const imageUrl = getAssetUrl(image);
  const imageAlt = image?.description || "";

  smoothScrollRef.current = store?.smoothScroll;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const titleElement = titleRef.current;
    const textElement = textRef.current;

    if (!section || !titleElement || !textElement) {
      return undefined;
    }

    const refresh = () => {
      if (smoothScrollRef.current) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const resize = () => {
      gsap.matchMediaRefresh();
      refresh();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const timeline = gsap.timeline({
          defaults: {
            duration: 1,
            ease: "power3.out",
          },
          scrollTrigger: {
            trigger: section,
            start: "top 70%",
            once: true,
            invalidateOnRefresh: true,
          },
        });

        timeline.fromTo(
          titleElement,
          {
            x: -80,
            autoAlpha: 0,
          },
          {
            x: 0,
            autoAlpha: 1,
          },
        );

        timeline.fromTo(
          textElement,
          {
            y: 80,
            autoAlpha: 0,
          },
          {
            y: 0,
            autoAlpha: 1,
          },
          "-=0.55",
        );

        requestAnimationFrame(refresh);

        return () => {
          timeline.kill();
        };
      });

      mm.add("(max-width:1023px)", () => {
        gsap.set([titleElement, textElement], {
          clearProps: "all",
        });
      });

      requestAnimationFrame(refresh);
    }, section);

    window.addEventListener("load", refresh);
    window.addEventListener("resize", resize);

    return () => {
      ctx.revert();

      window.removeEventListener("load", refresh);
      window.removeEventListener("resize", resize);

      ScrollTrigger.clearScrollMemory();
    };
  }, []);

  return (
    <TeamInfoMain
      ref={sectionRef}
      id={sectionId}
      aria-labelledby={`${sectionId}-title`}
    >
      <TeamInfoContent>
        <TeamInfoTitle
          ref={titleRef}
          id={`${sectionId}-title`}
        >
          <span>Our</span>

          <strong>Team</strong>
        </TeamInfoTitle>

        <TeamInfoText ref={textRef}>
          <RichText content={title} />
        </TeamInfoText>
      </TeamInfoContent>

      <TeamInfoImage>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={imageAlt}
            loading="lazy"
          />
        )}
      </TeamInfoImage>
    </TeamInfoMain>
  );
};

OurTeamInfo.propTypes = {
  id: PropTypes.string,
  title: PropTypes.object,
  image: PropTypes.shape({
    description: PropTypes.string,
    file: PropTypes.shape({
      url: PropTypes.string,
      contentType: PropTypes.string,
    }),
  }),
};

OurTeamInfo.defaultProps = {
  id: "our-team",
  title: null,
  image: null,
};

export default OurTeamInfo;